import React from "react";
import styled, { keyframes } from 'styled-components';
import TopBarNav from '../styled/Topbar';

const gradientShift = keyframes`
  0%   { background-position: 0% 50%; }
  50%  { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.4; }
  50%      { opacity: 0.8; }
`;

const PageWrapper = styled.div`
  min-height: 100vh;
  overflow-x: hidden;
  background: linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%);
  background-size: 200% 200%;
  animation: ${gradientShift} 15s ease infinite;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 1rem 4rem;
`;

/* Decorative floating orbs — matching homepage */
const OrbContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 0;
  overflow: hidden;
`;

const Orb = styled.div`
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  animation: ${pulse} 6s ease-in-out infinite;

  &:nth-child(1) {
    width: 450px; height: 450px;
    background: rgba(99, 102, 241, 0.2);
    top: -8%; left: -8%;
    animation-delay: 0s;
  }
  &:nth-child(2) {
    width: 350px; height: 350px;
    background: rgba(236, 72, 153, 0.15);
    bottom: -5%; right: -6%;
    animation-delay: 2s;
  }
  &:nth-child(3) {
    width: 280px; height: 280px;
    background: rgba(52, 211, 153, 0.12);
    top: 50%; left: 55%;
    animation-delay: 4s;
  }
`;

const ContentCard = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 52rem;
  margin-top: 6rem;
  animation: ${fadeInUp} 0.7s ease-out;

  background: rgba(255, 255, 255, 0.04);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 24px;
  padding: 3rem 2.8rem;

  @media (max-width: 640px) {
    padding: 2rem 1.4rem;
    margin-top: 5rem;
    border-radius: 18px;
  }

  /* ─── Typography ─── */

  h1 {
    font-size: clamp(1.8rem, 4vw, 2.4rem);
    font-weight: 700;
    background: linear-gradient(135deg, #ffffff 0%, #a5b4fc 50%, #f0abfc 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin-bottom: 1.5rem;
    letter-spacing: -0.01em;
    line-height: 1.3;
  }

  h2 {
    font-size: clamp(1.15rem, 2.5vw, 1.4rem);
    font-weight: 600;
    color: #c7d2fe;
    margin-top: 2rem;
    margin-bottom: 0.6rem;
    letter-spacing: -0.005em;
  }

  p {
    font-size: 0.95rem;
    color: rgba(255, 255, 255, 0.6);
    line-height: 1.75;
    margin-bottom: 0.8rem;
  }

  strong {
    display: block;
    font-size: 1.05rem;
    font-weight: 600;
    color: #c7d2fe;
    margin-top: 1.8rem;
    margin-bottom: 0.4rem;
  }

  ul {
    list-style: none;
    padding: 0;
    margin: 0.5rem 0 1rem;
  }

  li {
    position: relative;
    padding-left: 1.6rem;
    margin-bottom: 0.6rem;
    font-size: 0.95rem;
    color: rgba(255, 255, 255, 0.6);
    line-height: 1.7;

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0.55em;
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: linear-gradient(135deg, #6366f1, #a855f7);
    }
  }

  a {
    color: #a5b4fc;
    text-decoration: none;
    font-weight: 500;
    transition: color 0.2s;
    border-bottom: 1px solid rgba(165, 180, 252, 0.3);

    &:hover {
      color: #c7d2fe;
      border-bottom-color: rgba(199, 210, 254, 0.5);
    }
  }

  img {
    max-width: 100%;
    border-radius: 12px;
  }

  button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 0.7rem 2rem;
    border: none;
    border-radius: 12px;
    background: linear-gradient(135deg, #6366f1, #8b5cf6, #a855f7);
    color: white;
    font-size: 0.95rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 4px 20px rgba(99, 102, 241, 0.35);

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 28px rgba(99, 102, 241, 0.5);
    }

    &:active {
      transform: translateY(0);
    }
  }
`;

/* ─── Divider line between sections ─── */
const Divider = styled.hr`
  border: none;
  height: 1px;
  background: rgba(255, 255, 255, 0.06);
  margin: 2rem 0;
`;

const Footer = styled.footer`
  position: relative;
  z-index: 1;
  text-align: center;
  padding: 2.5rem 1rem 0;
  margin-top: 1rem;
`;

const FooterText = styled.p`
  color: rgba(255, 255, 255, 0.25);
  font-size: 0.8rem;
`;

const PageTemplate = ({ children }) => {
  return (
    <PageWrapper>
      <OrbContainer>
        <Orb /><Orb /><Orb />
      </OrbContainer>

      <TopBarNav onSignInClick={() => {}} />

      <ContentCard>
        {children}
      </ContentCard>

      <Footer>
        <FooterText>© {new Date().getFullYear()} BabbleOn. All rights reserved.</FooterText>
      </Footer>
    </PageWrapper>
  );
};

export default PageTemplate;
